// Order costing helpers — shared by the order form, the orders table and the
// production report so the same figure is never computed two different ways.
//
// CMT orders: the client supplies materials, so conversion cost (payroll +
// outsourcing) IS the cost. FOB orders: the factory buys the materials, so the
// true cost is conversion + materials.
//
// Material rows record quantities for the WHOLE order, not per piece —
// "753 m of denim", not "1.4 m per piece". Cost per piece is always derived
// from the order quantity at render time and never stored, so it stays correct
// when the order quantity is later edited.

export const isFob = (order) => order?.order_type === 'fob';

/** Cost of a single material row: qty x unit price. */
export const materialRowCost = (row) =>
  (parseFloat(row?.qty) || 0) * (parseFloat(row?.unit_price) || 0);

/** Total material spend for an order. */
export const materialsTotal = (materials) =>
  (materials || []).reduce((sum, row) => sum + materialRowCost(row), 0);

/** Total pieces on an order, summed across its size/colour variants. */
export const orderQuantity = (order) =>
  (order?.variants || []).reduce((sum, v) => sum + (v.quantity || 0), 0);

/** Material cost attributable to one piece of this order. */
export const materialCostPerPiece = (order) => {
  const qty = orderQuantity(order);
  if (!qty) return 0;
  return materialsTotal(order?.materials) / qty;
};

/**
 * True when an FOB order has no materials recorded.
 *
 * This matters: such an order would otherwise cost as conversion-only and
 * silently understate the very figure this feature exists to fix. Callers use
 * it to warn, never to block — shipping before the fabric invoice arrives is
 * normal, and past orders are backfilled gradually.
 */
export const needsMaterials = (order) =>
  isFob(order) && materialsTotal(order?.materials) <= 0;

/** Common units offered as a datalist; free text is still allowed. */
export const MATERIAL_UNITS = ['m', 'pc', 'kg', 'cone', 'roll', 'yd', 'set'];
